import React, { useState, useEffect } from 'react';
import { API } from '../App';

const SEV_META = {
  critical: { color: '#ef4444', bg: '#ef444415', border: '#ef444440', icon: '🔴', label: 'Kritisch' },
  warning:  { color: '#f59e0b', bg: '#f59e0b15', border: '#f59e0b40', icon: '🟡', label: 'Warnung'  },
  info:     { color: '#3b82f6', bg: '#3b82f615', border: '#3b82f640', icon: '🔵', label: 'Info'     },
};

const TYPE_LABELS = {
  lc_expiring:    'LC läuft ab (7 Tage)',
  lc_expiring_30: 'LC läuft ab (30 Tage)',
  deal_no_docs:   'Deal ohne Dokumente',
  kyc_doc_expiring: 'KYC-Dokument läuft ab',
};

export default function AlertsPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [filter, setFilter]   = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/alerts');
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSend = async () => {
    setSending(true); setSendResult(null);
    try {
      const r = await API.post('/alerts/send');
      setSendResult(r.data);
    } catch (e) {
      setSendResult({ error: e.response?.data?.detail || 'Fehler beim Senden' });
    } finally { setSending(false); }
  };

  const filtered = (data?.alerts || []).filter(a =>
    !filter || a.severity === filter
  );

  return (
    <div className="page-content">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Milestone Alerts</h1>
          <p>LC-Abläufe, fehlende Dokumente, KYC-Fristen</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load}>⟳ Aktualisieren</button>
          {data?.telegram_configured && (
            <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={sending}>
              {sending ? 'Sende…' : '📨 Telegram senden'}
            </button>
          )}
        </div>
      </div>

      {/* Telegram Status */}
      {data && !data.telegram_configured && (
        <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8,
          background: '#60706015', border: '1px solid #60706030', fontSize: 12, color: 'var(--text3)' }}>
          💬 Telegram nicht konfiguriert — setze <code>TELEGRAM_BOT_TOKEN</code> und <code>TELEGRAM_CHAT_ID</code> in der .env Datei.
        </div>
      )}

      {/* Send Result */}
      {sendResult && (
        <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8,
          background: sendResult.error ? '#ef444415' : '#22c55e15',
          border: `1px solid ${sendResult.error ? '#ef444440' : '#22c55e40'}`,
          fontSize: 13, color: sendResult.error ? '#ef4444' : '#22c55e' }}>
          {sendResult.error
            ? `Fehler: ${sendResult.error}`
            : `✓ Telegram gesendet — ${sendResult.alerts_found} Alerts`}
        </div>
      )}

      {/* Summary Cards */}
      {data && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[
            { key: 'critical', label: 'Kritisch', value: data.critical },
            { key: 'warning',  label: 'Warnungen', value: data.warnings  },
            { key: '',         label: 'Gesamt',    value: data.total     },
          ].map(s => {
            const m = SEV_META[s.key] || { color: 'var(--accent)', bg: 'var(--bg-card)', border: 'var(--border)' };
            return (
              <div key={s.label} onClick={() => setFilter(filter === s.key ? '' : s.key)}
                style={{ padding: '14px 20px', borderRadius: 10, cursor: 'pointer',
                  background: m.bg, border: `1px solid ${filter === s.key ? m.color : m.border}`,
                  textAlign: 'center', minWidth: 100 }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: m.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{s.label}</div>
              </div>
            );
          })}
        </div>
      )}

      {loading && <div className="loading">Prüfe Alerts…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)' }}>Keine Alerts</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Alle Fristen und Dokumente in Ordnung.</div>
        </div>
      )}

      {/* Alert List */}
      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((alert, i) => {
            const m = SEV_META[alert.severity] || SEV_META.info;
            return (
              <div key={i} style={{ padding: '12px 16px', borderRadius: 10,
                background: m.bg, border: `1px solid ${m.border}`,
                display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 18, marginTop: 1 }}>{m.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: m.color }}>
                      {alert.title}
                    </span>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4,
                      background: m.color + '33', color: m.color, fontWeight: 700 }}>
                      {TYPE_LABELS[alert.type] || alert.type}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>{alert.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
